/**
 * Which part of the store a path belongs to when the store runs a code theme.
 *
 * Location: src/code-theme
 * Why: money and account pages never load anything of the theme
 * (docs/tema-em-codigo, 3.5). They open as a new document, so the boundary
 * has to be decided from the path alone, before the theme is downloaded.
 * React Router matches paths ignoring case and decoding them, so this does too.
 */

/** Pages a code theme can write. Same list as THEME_PAGES in the API. */
export const THEME_PAGES = ['home', 'shop', 'category', 'product', 'search', 'news', 'post', 'page', 'players', '404', 'maintenance'] as const
export type ThemePageName = (typeof THEME_PAGES)[number]

/** Cart, payment, account, password and sign in: always without the theme. */
const ACCOUNT_PATHS = ['/carrinho', '/payment', '/perfil', '/minhas-compras', '/redefinir-senha', '/entrar']

/** Our own pages, shown with our content inside the frame of the theme. */
const OWN_PATHS = [
  '/suporte',
  '/presentes',
  '/parceiro',
  '/wiki',
  '/download',
  '/changelog',
  '/forum',
  '/formularios',
  '/minhas-respostas',
  '/regras',
  '/termos',
  '/privacidade',
  '/coleta-de-dados',
  '/discord',
  '/noticias/publicar',
]

export type DocumentKind = 'account' | 'storefront'

export interface ThemeRoute {
  page: ThemePageName
  params: Record<string, string>
}

function decode(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function segmentsOf(pathname: string): string[] {
  return pathname.split('/').filter(Boolean).map(decode)
}

/** Lower case, decoded, single slashes, no trailing slash. */
export function normalizePath(pathname: string): string {
  const path = '/' + segmentsOf(pathname).join('/').toLowerCase()
  return path.replace(/\/{2,}/g, '/')
}

function under(path: string, prefix: string): boolean {
  return path === prefix || path.indexOf(prefix + '/') === 0
}

export function documentKind(pathname: string): DocumentKind {
  const path = normalizePath(pathname)
  return ACCOUNT_PATHS.some((p) => under(path, p)) ? 'account' : 'storefront'
}

/**
 * The theme page for a path, or null when the page is ours (forum, wiki...)
 * or an account page. Anything unknown is the theme's 404.
 */
export function themeRouteFor(pathname: string): ThemeRoute | null {
  const path = normalizePath(pathname)
  if (documentKind(path) === 'account' || OWN_PATHS.some((p) => under(path, p))) return null
  const s = segmentsOf(pathname)
  const head = (s[0] || '').toLowerCase()
  if (s.length === 0) return { page: 'home', params: {} }
  if (head === 'loja') {
    if (s.length === 1) return { page: 'shop', params: {} }
    if (s.length === 2) return { page: 'shop', params: { server_id: s[1] } }
    if (s.length === 3) return { page: 'category', params: { server_id: s[1], category_slug: s[2] } }
    if (s.length === 5 && s[3].toLowerCase() === 'produto') return { page: 'product', params: { server_id: s[1], category_slug: s[2], product_slug: s[4] } }
  }
  if (head === 'busca' && s.length === 1) return { page: 'search', params: {} }
  if (head === 'noticias' && s.length === 1) return { page: 'news', params: {} }
  if ((head === 'noticias' || head === 'noticia') && s.length === 2) return { page: 'post', params: { slug: s[1] } }
  if (head === 'p' && s.length === 2) return { page: 'page', params: { slug: s[1] } }
  if (head === 'jogadores' && s.length === 1) return { page: 'players', params: {} }
  return { page: '404', params: {} }
}

/**
 * A link of the theme that the store can follow without reloading: same
 * origin, a path, not an account page. Account pages always load a new document.
 */
export function inAppPath(href: string, origin: string): string | null {
  let url: URL
  try {
    url = new URL(href, origin)
  } catch {
    return null
  }
  if (url.origin !== origin) return null
  if (documentKind(url.pathname) === 'account') return null
  return url.pathname + url.search + url.hash
}

/** Where to go back after signing in: a path of this store, never another site. */
export function safeNextPath(next: string | null | undefined): string {
  if (!next || next.charAt(0) !== '/' || next.charAt(1) === '/' || next.charAt(1) === '\\') return '/'
  return /^[\x20-\x7e]{1,300}$/.test(next) ? next : '/'
}

/**
 * Tokens of a preview link (`/__theme-preview/<token>`): signed by the API for
 * the dashboard editor, or `hys_pv_` + 43 characters made by the hystore tool.
 * Same shapes the API accepts; anything else is not sent to it.
 */
export const PREVIEW_TOKEN_RE = /^(?:hys_pv_[0-9A-Za-z]{43}|[A-Za-z0-9_-]{10,120}\.[A-Za-z0-9_-]{20,80})$/
